import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiError, apiFetch, post } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { keys } from "@/hooks/keys";
import { GATE_READ_RETRY } from "@/hooks/use-setup-journey";
import { API_BASE } from "@/lib/utils";
import { useAuth } from "@/lib/auth";

/** One home-screen chip. `label` is what fits the 34px pill; `prompt` is what
 *  the orchestrator receives when it is clicked. */
/** `reason` is the one line the onboarding cases show under each; rows
 *  generated before it existed have none. */
export type OrgSuggestion = { label: string; prompt: string; reason?: string };

type SuggestionsPayload = {
  suggestions?: unknown;
  writing?: unknown;
};

/** What GET /me/suggestions answers: the set, and whether one is on its way. */
export interface OrgSuggestionsState {
  suggestions: OrgSuggestion[];
  /** A set is being written. False once one is stored, and once the last run
   *  wrote nothing: nothing is coming, so nothing should say "loading". */
  writing: boolean;
}

/** The stored set predates the {label, prompt} split, so a plain string still
 *  reads back — as a chip whose label and prompt are the same text. */
function normalizeSuggestion(raw: unknown): OrgSuggestion | null {
  if (typeof raw === "string") {
    const s = raw.trim();
    return s ? { label: s, prompt: s } : null;
  }
  if (!raw || typeof raw !== "object") return null;
  const o = raw as { label?: unknown; prompt?: unknown; reason?: unknown };
  const label = typeof o.label === "string" ? o.label.trim() : "";
  const prompt = typeof o.prompt === "string" ? o.prompt.trim() : "";
  const reason = typeof o.reason === "string" ? o.reason.trim() : "";
  if (!label && !prompt) return null;
  return { label: label || prompt, prompt: prompt || label, ...(reason ? { reason } : {}) };
}

/** Answers saved so far, for resuming the signup questions mid-way. */
export type SignupAnswers = {
  heardFrom?: string;
  jobRole?: string;
  useCase?: string;
  /** Which AI tools they use: tool ids, "other", or "none". */
  aiTools?: string[];
};

export type OnboardingStatus = {
  companyDomain?: string;
  agentOnboardingDomain?: string;
  isPersonalEmail?: boolean;
  /** True once this person has answered the signup questions, or predates them. */
  completed?: boolean;
  /** A competitor, disposable or unverifiable address. Recorded for follow-up;
   *  the dashboard lets the person in regardless. */
  blocked?: boolean;
  savedState?: SignupAnswers & { currentStep?: string };
};

/**
 * The four personalized home-screen suggestions for the caller's org.
 * Returns an empty array when nobody in the org has onboarded, or when the
 * background generation hasn't produced a set yet — callers fall back to a
 * hardcoded default in that case.
 *
 * The GET triggers server-side regeneration (fire-and-forget) when the stored
 * set is missing or stale, so we poll on a short interval while it is still
 * empty and the fresh set appears without a manual reload.
 */
function useOrgSuggestionsQuery<T>(select: (state: OrgSuggestionsState) => T) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery<OrgSuggestionsState, Error, T>({
    queryKey: keys.onboarding.suggestions(orgId),
    queryFn: async () => {
      try {
        const data = await apiFetch<SuggestionsPayload>(`${API_BASE}/v1/me/suggestions`);
        const raw = data?.suggestions;
        const suggestions = Array.isArray(raw)
          ? raw.map(normalizeSuggestion).filter((s): s is OrgSuggestion => s !== null)
          : [];
        // An api that predates the flag says nothing, which reads as "maybe":
        // the tour then waits out its own window, as it always did.
        const writing = typeof data?.writing === "boolean" ? data.writing : suggestions.length === 0;
        return { suggestions, writing };
      } catch (err) {
        // 403 is a below-operator caller, who never renders these anyway.
        if (err instanceof ApiError && (err.status === 404 || err.status === 403)) return { suggestions: [], writing: false };
        throw err;
      }
    },
    select,
    staleTime: 60 * 1000,
    retry: false,
    // BI4: this used to poll every 8s forever until it got exactly 4
    // suggestions — if the background generation never produces them (e.g.
    // no AI Gateway credential configured), the caller's loading skeleton
    // never resolves, showing as "possibly stuck" indefinitely. Capping at
    // 5 attempts (~40s) lets the caller fall back to generic examples.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && (data.suggestions.length === 4 || !data.writing)) return false;
      if (query.state.dataUpdateCount >= 5) return false;
      return 8000;
    },
    refetchIntervalInBackground: false,
  });
}

/**
 * The four personalized home-screen suggestions for the caller's org.
 * Returns an empty array when nobody in the org has onboarded, or when the
 * background generation hasn't produced a set yet — callers fall back to a
 * hardcoded default in that case.
 *
 * The GET triggers server-side regeneration (fire-and-forget) when the stored
 * set is missing or stale, so we poll on a short interval while it is still
 * empty and the fresh set appears without a manual reload.
 */
export function useOrgSuggestions() {
  return useOrgSuggestionsQuery(selectSuggestions);
}

/** The set and whether one is on its way, off the same query. */
export function useOrgSuggestionsState() {
  return useOrgSuggestionsQuery(selectState);
}

const selectSuggestions = (state: OrgSuggestionsState) => state.suggestions;
const selectState = (state: OrgSuggestionsState) => state;

export function useOnboardingStatus(opts?: { enabled?: boolean }) {
  return useQuery<OnboardingStatus>({
    enabled: opts?.enabled ?? true,
    queryKey: keys.onboarding.status(),
    queryFn: async () => {
      try {
        const data = await apiFetch<OnboardingStatus>(`${API_BASE}/v1/onboarding/status`);
        return data ?? {};
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return {};
        throw err;
      }
    },
    staleTime: 60_000,
    // A failed read must not stand in for "answered": keep asking until it
    // answers, and the gate puts the questions back the moment it does.
    ...GATE_READ_RETRY,
  });
}

export function useConfirmOnboardingDomain() {
  return useMutation({
    mutationFn: (domain: string) =>
      apiFetch(`${API_BASE}/v1/onboarding/agent-domain`, {
        method: "POST",
        body: JSON.stringify({ domain }),
      }),
  });
}

/** Stores one answer as the person moves on, so a reload resumes where they were. */
export function useSaveSignupAnswer() {
  return useMutation({
    mutationFn: (patch: SignupAnswers & { currentStep: string }) =>
      apiFetch(`${API_BASE}/v1/onboarding`, { ...post(patch), method: "PATCH" }),
  });
}

/** Stores all four answers. The api sends them to PostHog as one event. */
export function useSubmitSignupAnswers() {
  return useMutation({
    mutationFn: (answers: Required<Omit<SignupAnswers, "aiTools">> & { aiTools: string[] }) =>
      apiFetch(`${API_BASE}/v1/onboarding`, post(answers)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.onboarding.status() }),
  });
}
